type SourceProduct = {
  evidence?: Record<string, any>;
  specifications?: Record<string, any>;
};

const stableId = (value: unknown): value is string =>
  typeof value === 'string' && /^[a-zA-Z0-9][a-zA-Z0-9_-]{0,79}$/.test(value);

const externalLink = (value: unknown): string | undefined => {
  if (typeof value !== 'string' || !/^https?:\/\//i.test(value)) return undefined;
  try {
    const parsed = new URL(value);
    if (parsed.username || parsed.password || parsed.pathname === '/') return undefined;
    return parsed.href;
  } catch {
    return undefined;
  }
};

export const isCatalogApiLink = (href: string, apiBaseUrl = ''): boolean => {
  try {
    const target = new URL(href, window.location.origin);
    const apiOrigin = new URL(apiBaseUrl || window.location.origin, window.location.origin).origin;
    return target.origin === apiOrigin
      && /^\/api\/catalogs\/(?:(?:unicor|carpet)\/)?[a-zA-Z0-9_-]+$/.test(target.pathname);
  } catch {
    return false;
  }
};

/** HTML navigations bypass CRA's API proxy. Fetch the authenticated PDF first. */
export async function openCatalogSourcePdf(href: string): Promise<void> {
  const tab = window.open('about:blank', '_blank');
  if (!tab) throw new Error('Allow pop-ups to open the catalog in a new tab.');
  let blobUrl: string | undefined;
  try {
    tab.opener = null;
    const [url, fragment] = href.split('#');
    const response = await fetch(url, { credentials: 'include', headers: { Accept: 'application/pdf' } });
    if (!response.ok || !/^application\/pdf(?:;|$)/i.test(response.headers.get('Content-Type') || '')) {
      throw new Error('Unable to open the catalog. Please sign in again or retry.');
    }
    const pdf = await response.blob();
    if (!/^application\/pdf(?:;|$)/i.test(pdf.type)) {
      throw new Error('Unable to open the catalog. Please sign in again or retry.');
    }
    if (tab.closed) return;
    blobUrl = URL.createObjectURL(pdf);
    tab.location.href = `${blobUrl}${fragment ? `#${fragment}` : ''}`;
    // PDF viewers may read the blob after navigation; retain it until the tab closes.
    const cleanup = window.setInterval(() => {
      if (tab.closed) {
        URL.revokeObjectURL(blobUrl!);
        window.clearInterval(cleanup);
      }
    }, 30000);
  } catch {
    if (blobUrl) URL.revokeObjectURL(blobUrl);
    tab.close();
    throw new Error('Unable to open the catalog. Please sign in again or retry.');
  }
}

/** Stable registry routes only: never interpret an evidence file path as a browser URL. */
export function productSourceLink(product: SourceProduct, apiBaseUrl = ''): string | undefined {
  const evidence = product.evidence || {};
  const source = String(evidence.source || '');
  const sourceCatalogId = source.match(/^catalog:([a-zA-Z0-9_-]+)$/)?.[1];
  const catalogId = evidence.catalog_id || product.specifications?.catalog_id || sourceCatalogId;
  const isPdf = evidence.doc_type === 'catalog_pdf' || source === 'catalog' || source.startsWith('catalog:') || Boolean(catalogId);
  const base = apiBaseUrl.replace(/\/+$/, '');
  let href: string | undefined;
  if (isPdf && stableId(catalogId)) {
    href = `${base}/api/catalogs/${encodeURIComponent(catalogId)}`;
  } else if (isPdf && source === 'unicor' && stableId(evidence.document_id)) {
    href = `${base}/api/catalogs/unicor/${encodeURIComponent(evidence.document_id)}`;
  } else if (isPdf && source === 'carpet_ii' && stableId(evidence.document_id)) {
    href = `${base}/api/catalogs/carpet/${encodeURIComponent(evidence.document_id)}`;
  } else if (isPdf) {
    href = externalLink(evidence.catalog_url) || externalLink(evidence.url);
  } else {
    href = externalLink(evidence.product_url) || externalLink(evidence.url);
  }
  if (href && isPdf && Number.isInteger(evidence.page) && evidence.page > 0) {
    href = `${href.split('#')[0]}#page=${evidence.page}`;
  }
  return href;
}
